/// Frame-rate-independent physics tuned to match the original 60 Hz feel.
///
/// Line-for-line transcription of mobile/lib/game/game_physics.dart. Every
/// constant and every formula must stay identical to the Dart original, so
/// change this file only alongside that one. gamePhysics.test.ts is the port
/// of game_physics_test.dart and guards the numbers.
///
/// Design jump arc (Chrome T-Rex character at 60 fps):
///   apex height ~ 88.3 px, air time ~ 0.572 s
/// Continuous equivalents under constant gravity (y+ down):
///   g  = 8H / T^2
///   v0 = -4H / T

/// Target refresh rate the original per-frame constants were authored against.
export const targetFps = 60.0;

/// Fixed gameplay step. Rendering stays on the variable frame clock.
export const fixedTimeStep = 1.0 / 240.0;

/// Cap accumulated simulation time so a hitch cannot tunnel objects.
export const maxAccumulator = 0.25;

// --- Chrome-style base speed (internal units ~ px/frame at 60 Hz) ---

export const initialBaseSpeed = 6.0;
export const maxBaseSpeed = 13.0;

/// Per-second ramp of base speed. `0.001 * 60` preserves the ~117 s climb
/// from 6 to 13 on every device.
export const accelerationPerSecond = 0.06;

/// Convert internal base speed to world px/s.
export function baseSpeedToPixelsPerSecond(baseSpeed: number): number {
  return baseSpeed * targetFps;
}

export const initialGameSpeed = initialBaseSpeed * targetFps; // 360
export const maxGameSpeed = maxBaseSpeed * targetFps; // 780

// --- Jump design constants (tunable) ---

export const jumpApexHeight = 88.3;
export const jumpAirTime = 0.572;

/// Positive because the y-axis points down.
export const dinoGravity = (8.0 * jumpApexHeight) / (jumpAirTime * jumpAirTime);

/// Negative = upward launch.
export const dinoJumpVelocity = (-4.0 * jumpApexHeight) / jumpAirTime;

/// Fast-fall when ducking mid-air. Chrome: `gravity * 12` px/frame -> px/s.
export const dinoDropVelocity = (dinoGravity * 12.0) / targetFps;

/// Chrome `gapCoefficient` (normal mode). Applied to each type's minGap.
export const gapCoefficient = 0.6;

/// Chrome `maxGapCoefficient`. maxGap = round(minGap * this).
export const maxGapCoefficient = 1.5;

export const dinoStartX = 50.0;
export const scoreInterval = 50.0;

export interface JumpStep {
  y: number;
  velocity: number;
}

/// Closed-form constant-gravity step (exact for any dt).
export function integrateJumpStep(options: {
  y: number;
  velocity: number;
  gravity: number;
  dt: number;
}): JumpStep {
  const { y, velocity, gravity, dt } = options;
  return {
    y: y + velocity * dt + 0.5 * gravity * dt * dt,
    velocity: velocity + gravity * dt,
  };
}

/// Pure speed-ramp helper for tests / single-sourced gameplay.
export function stepBaseSpeed(baseSpeed: number, dt: number): number {
  const next = baseSpeed + accelerationPerSecond * dt;
  return Math.min(Math.max(next, initialBaseSpeed), maxBaseSpeed);
}

/// Chrome `getGap` lower bound: `round(width * baseSpeed + typeMinGap * 0.6)`.
/// `baseSpeed` is the internal 6–13 unit, not px/s.
export function obstacleGapMin(options: {
  width: number;
  baseSpeed: number;
  typeMinGap: number;
}): number {
  const { width, baseSpeed, typeMinGap } = options;
  return Math.round(width * baseSpeed + typeMinGap * gapCoefficient);
}

/// Chrome upper bound: `round(minGap * 1.5)`.
export function obstacleGapMax(minGap: number): number {
  return Math.round(minGap * maxGapCoefficient);
}

/// Inclusive random gap in [min, max], matching Chrome `getRandomNum`.
/// `nextInt` is exclusive-max, like Dart `Random.nextInt`.
export function obstacleGap(options: {
  width: number;
  baseSpeed: number;
  typeMinGap: number;
  nextInt: (maxExclusive: number) => number;
}): number {
  const minGap = obstacleGapMin(options);
  const maxGap = obstacleGapMax(minGap);
  const span = maxGap - minGap;
  if (span <= 0) return minGap;
  return minGap + options.nextInt(span + 1);
}

export interface JumpSimulation {
  apexHeight: number;
  airTime: number;
}

/// Simulate a full jump from groundY until landing.
/// Returns apex height (upward displacement) and air time.
///
/// Uses the closed-form integrator and solves the exact landing time inside
/// the overshooting step so results are not quantized to dt.
export function simulateJump(options: {
  dt: number;
  groundY: number;
  gravity?: number;
  jumpVelocity?: number;
}): JumpSimulation {
  const { dt, groundY } = options;
  const g = options.gravity ?? dinoGravity;
  let y = groundY;
  let velocity = options.jumpVelocity ?? dinoJumpVelocity;
  let elapsed = 0.0;
  let minY = y;

  const maxSteps = Math.min(Math.max(Math.ceil(2.0 / dt), 1), 100000);
  for (let i = 0; i < maxSteps; i += 1) {
    const yBefore = y;
    const vBefore = velocity;
    const next = integrateJumpStep({ y, velocity, gravity: g, dt });
    y = next.y;
    velocity = next.velocity;

    // Continuous apex while ascending -> descending within this step.
    if (vBefore < 0 && velocity >= 0) {
      const timeToApex = -vBefore / g;
      const apexY = yBefore + vBefore * timeToApex + 0.5 * g * timeToApex * timeToApex;
      if (apexY < minY) minY = apexY;
    } else if (y < minY) {
      minY = y;
    }

    if (y >= groundY && elapsed + dt > 0) {
      // Solve yBefore + vBefore*t + 0.5*g*t^2 = groundY for t in (0, dt].
      const a = 0.5 * g;
      const b = vBefore;
      const c = yBefore - groundY;
      const discriminant = b * b - 4 * a * c;
      let landDt = dt;
      if (discriminant >= 0 && Math.abs(a) > 1e-12) {
        const sqrtD = Math.sqrt(discriminant);
        const t1 = (-b - sqrtD) / (2 * a);
        const t2 = (-b + sqrtD) / (2 * a);
        for (const t of [t1, t2]) {
          if (t > 1e-12 && t <= dt + 1e-12) {
            landDt = t;
            break;
          }
        }
      }
      return { apexHeight: groundY - minY, airTime: elapsed + landDt };
    }

    elapsed += dt;
  }
  return { apexHeight: groundY - minY, airTime: elapsed };
}

/// Simulate durationSeconds of the base-speed ramp at step dt.
export function simulateSpeedRamp(options: {
  dt: number;
  durationSeconds: number;
  startSpeed?: number;
}): number {
  const { dt, durationSeconds } = options;
  let speed = options.startSpeed ?? initialBaseSpeed;
  let elapsed = 0.0;
  while (elapsed + dt <= durationSeconds + 1e-12) {
    speed = stepBaseSpeed(speed, dt);
    elapsed += dt;
  }
  const remainder = durationSeconds - elapsed;
  if (remainder > 1e-12) {
    speed = stepBaseSpeed(speed, remainder);
  }
  return speed;
}
